import { useState } from 'react';
import type { CategorySummaryDto } from '../types';
import './StatusPage.css';

interface CategoryReportCardProps {
  category: CategorySummaryDto;
}

export function CategoryReportCard({ category }: CategoryReportCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const categoryDisplayName = category.subCategoryName
    ? `${category.categoryName} > ${category.subCategoryName}`
    : category.categoryName;

  const successRate = category.stats.articlesScraped > 0
    ? ((category.stats.articlesSuccessful / category.stats.articlesScraped) * 100).toFixed(1)
    : '0.0';

  const handleToggle = () => setIsExpanded(!isExpanded);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <div className="category-report-card">
      <div
        className="category-report-card__header"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="category-report-card__title-section">
          <h3 className="category-report-card__title">{categoryDisplayName}</h3>
          <div className="category-report-card__summary">
            {category.summary ? (
              <span className="category-report-card__status category-report-card__status--success">
                Summary Generated
              </span>
            ) : (
              <span className="category-report-card__status category-report-card__status--pending">
                No Summary
              </span>
            )}
            <span className="category-report-card__separator">•</span>
            <span>{category.stats.articlesSuccessful} articles</span>
            <span className="category-report-card__separator">•</span>
            <span>{category.stats.clusterCount} clusters</span>
          </div>
        </div>
        <button
          className="category-report-card__toggle"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          {isExpanded ? '−' : '+'}
        </button>
      </div>

      {isExpanded && (
        <div className="category-report-card__details">
          {/* Stats Section */}
          <div className="category-report-card__section">
            <h4 className="category-report-card__section-title">Pipeline Stats</h4>
            <div className="category-report-card__metrics">
              <div className="category-report-card__metric">
                <span className="category-report-card__metric-label">Sources:</span>
                <span className="category-report-card__metric-value">{category.stats.sourceCount}</span>
              </div>
              <div className="category-report-card__metric">
                <span className="category-report-card__metric-label">Items Fetched:</span>
                <span className="category-report-card__metric-value">{category.stats.rssItemsFetched}</span>
              </div>
              <div className="category-report-card__metric">
                <span className="category-report-card__metric-label">Items Selected:</span>
                <span className="category-report-card__metric-value">{category.stats.rssItemsSelected}</span>
              </div>
              <div className="category-report-card__metric">
                <span className="category-report-card__metric-label">Articles Scraped:</span>
                <span className="category-report-card__metric-value">{category.stats.articlesScraped}</span>
              </div>
              <div className="category-report-card__metric">
                <span className="category-report-card__metric-label">Successful:</span>
                <span className="category-report-card__metric-value category-report-card__metric-value--success">
                  {category.stats.articlesSuccessful}
                </span>
              </div>
              <div className="category-report-card__metric">
                <span className="category-report-card__metric-label">Success Rate:</span>
                <span className="category-report-card__metric-value">{successRate}%</span>
              </div>
              <div className="category-report-card__metric">
                <span className="category-report-card__metric-label">Clusters:</span>
                <span className="category-report-card__metric-value">{category.stats.clusterCount}</span>
              </div>
              <div className="category-report-card__metric">
                <span className="category-report-card__metric-label">Avg Interest Score:</span>
                <span className="category-report-card__metric-value">
                  {category.stats.averageInterestScore.toFixed(3)}
                </span>
              </div>
            </div>
          </div>

          {/* Summary Section */}
          {category.summary && (
            <div className="category-report-card__section">
              <h4 className="category-report-card__section-title">Summary</h4>
              <div className="category-report-card__summary-info">
                {category.summary.headline && (
                  <p className="category-report-card__headline">{category.summary.headline}</p>
                )}
                <div className="category-report-card__metrics">
                  <div className="category-report-card__metric">
                    <span className="category-report-card__metric-label">Model:</span>
                    <span className="category-report-card__metric-value">
                      {category.summary.modelUsed || 'Unknown'}
                    </span>
                  </div>
                  <div className="category-report-card__metric">
                    <span className="category-report-card__metric-label">Articles:</span>
                    <span className="category-report-card__metric-value">{category.summary.articlesProcessed}</span>
                  </div>
                  <div className="category-report-card__metric">
                    <span className="category-report-card__metric-label">Sources:</span>
                    <span className="category-report-card__metric-value">{category.summary.sourcesConsulted}</span>
                  </div>
                  <div className="category-report-card__metric">
                    <span className="category-report-card__metric-label">Clusters:</span>
                    <span className="category-report-card__metric-value">{category.summary.clustersProcessed}</span>
                  </div>
                  <div className="category-report-card__metric">
                    <span className="category-report-card__metric-label">Tokens:</span>
                    <span className="category-report-card__metric-value">
                      {category.summary.inputTokens.toLocaleString()} in / {category.summary.outputTokens.toLocaleString()} out
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contrast Section */}
          {category.contrast && (
            <div className="category-report-card__section">
              <h4 className="category-report-card__section-title">Contrast Summary</h4>
              <div className="category-report-card__summary-info">
                {category.contrast.headline && (
                  <p className="category-report-card__headline">{category.contrast.headline}</p>
                )}
                <div className="category-report-card__metrics">
                  <div className="category-report-card__metric">
                    <span className="category-report-card__metric-label">Model:</span>
                    <span className="category-report-card__metric-value">
                      {category.contrast.modelUsed || 'Unknown'}
                    </span>
                  </div>
                  <div className="category-report-card__metric">
                    <span className="category-report-card__metric-label">Tokens:</span>
                    <span className="category-report-card__metric-value">
                      {category.contrast.inputTokens.toLocaleString()} in / {category.contrast.outputTokens.toLocaleString()} out
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
